import React, { useState } from "react";
import { queryCache } from "react-query";
import {
  EuiCallOut,
  EuiConfirmModal,
  EuiFormRow,
  EuiSpacer,
  EuiText,
  EuiTextArea,
} from "@elastic/eui";
import {
  approveRelease,
  pauseRelease,
  promoteRelease,
  releaseToAll,
  resumeRelease,
  rollbackRelease,
  setReleaseOutlets,
  setSafeToRollBack,
  startRelease,
} from "../../api/appReleases";
import { getErrorMessage, RELEASE_QUERY_KEY } from "./helpers";

const getPromoteTarget = (release) => (release.stage === 10 ? "50%" : "100%");

// Every action the backend can list in allowed_actions, except where noted.
export const ACTIONS = {
  start: {
    label: () => "Start rollout",
    color: "primary",
    fill: true,
    body: () =>
      "The server will pick 10% of eligible outlets at random. They will start updating on their next check.",
    run: (release) => startRelease(release.id),
  },
  promote: {
    label: (release) => `Promote to ${getPromoteTarget(release)}`,
    color: "primary",
    fill: true,
    body: (release) =>
      release.stage === 10
        ? "Outlets already picked stay on the new version, and more are picked at random to reach 50%."
        : "This sends the release for approval. A second admin must approve going to 100%.",
    run: (release, { reason }) => promoteRelease(release.id, reason),
  },
  approve: {
    label: () => "Approve",
    color: "primary",
    fill: true,
    body: (release) =>
      `Approve ${release.version} to be published to every outlet. You must be a different admin from the one who requested it.`,
    run: (release) => approveRelease(release.id),
  },
  release_all: {
    label: () => "Release to all",
    color: "primary",
    fill: false,
    body: (release) =>
      `This moves ${release.version} to publish, so that every outlet gets it.`,
    run: (release) => releaseToAll(release.id),
  },
  pause: {
    label: () => "Pause",
    color: "warning",
    fill: false,
    body: () =>
      "No new outlet will get this version. Outlets already on it keep running it.",
    run: (release) => pauseRelease(release.id),
  },
  resume: {
    label: () => "Resume",
    color: "primary",
    fill: false,
    body: () => "Picked outlets will continue updating to this version.",
    run: (release) => resumeRelease(release.id),
  },
  rollback: {
    label: () => "Roll back",
    color: "danger",
    fill: false,
    body: (release) =>
      release.status === "publish" || release.status === "stable"
        ? `This blocks ${release.version}. After 100% it needs a second admin, then npm run release:rollback.`
        : `This blocks ${release.version}. Picked outlets send their orders, keep their data, and go back to the old version when no bill is open.`,
    run: (release, { reason }) => rollbackRelease(release.id, reason),
  },
  // Not from allowed_actions: the summary shows this toggle next to the rollback state.
  safe_to_roll_back: {
    label: (release) =>
      release.safe_to_roll_back === false
        ? "Mark safe to roll back"
        : "Mark not safe to roll back",
    color: "warning",
    fill: false,
    body: (release) =>
      release.safe_to_roll_back === false
        ? `Roll back will be allowed again for ${release.version}.`
        : `Roll back will be refused for ${release.version}. Use this when going back would break data, so problems must be fixed forward with a new version.`,
    run: (release, { reason }) =>
      setSafeToRollBack(release.id, release.safe_to_roll_back === false, reason),
  },
  // Not shown in the action bar: the outlet picker opens it with its own list.
  set_outlets: {
    label: () => "Release to selected",
    color: "primary",
    fill: true,
    body: () => "Only the ticked outlets will get this version.",
    run: (release, { outletIds }) => setReleaseOutlets(release.id, outletIds),
  },
};

const isReasonRequired = (action, release) =>
  action === "rollback" ||
  action === "safe_to_roll_back" ||
  (action === "promote" && release.reason_required);

const ActionDialog = ({
  action,
  release,
  outletIds,
  warning,
  onClose,
  onDone,
}) => {
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const config = ACTIONS[action];
  const label = config.label(release);
  const needsReason = isReasonRequired(action, release);
  const trimmedReason = reason.trim();
  const showNotSafe = action === "rollback" && !release.safe_to_roll_back;

  const onConfirm = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await config.run(release, { reason: trimmedReason, outletIds });
      queryCache.invalidateQueries(RELEASE_QUERY_KEY);
      if (onDone) onDone();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
      setIsLoading(false);
    }
  };

  return (
    <EuiConfirmModal
      title={`${label}: ${release.version}`}
      onCancel={onClose}
      onConfirm={onConfirm}
      cancelButtonText="Cancel"
      confirmButtonText={label}
      buttonColor={config.color === "danger" ? "danger" : "primary"}
      isLoading={isLoading}
      confirmButtonDisabled={needsReason && !trimmedReason}
    >
      <EuiText size="s">
        <p>{config.body(release)}</p>
      </EuiText>
      {action === "promote" && release.reason_required ? (
        <>
          <EuiSpacer size="m" />
          <EuiCallOut
            size="s"
            color="warning"
            iconType="alert"
            title={`This stage has run for ${release.days_in_stage} days. The suggested wait is at least ${
              release.suggested_wait_days
                ? release.suggested_wait_days.min
                : 7
            } days.`}
          />
        </>
      ) : null}
      {showNotSafe ? (
        <>
          <EuiSpacer size="m" />
          <EuiCallOut
            size="s"
            color="danger"
            iconType="alert"
            title="This release is marked not safe to roll back. Fix forward with a new version."
          />
        </>
      ) : null}
      {warning ? (
        <>
          <EuiSpacer size="m" />
          <EuiCallOut size="s" color="warning" iconType="alert" title={warning} />
        </>
      ) : null}
      {needsReason ? (
        <>
          <EuiSpacer size="m" />
          <EuiFormRow label="Reason (required)" fullWidth>
            <EuiTextArea
              fullWidth
              rows={3}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </EuiFormRow>
        </>
      ) : null}
      {error ? (
        <>
          <EuiSpacer size="m" />
          <EuiCallOut size="s" color="danger" iconType="alert" title={error} />
        </>
      ) : null}
    </EuiConfirmModal>
  );
};

export default ActionDialog;
